// Wraps Base64Field + a ref TextField. The textarea holds plaintext
// but the value submitted to the API is already base64; never display
// secret material in any other surface.

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { tenantSecrets } from "../../api/endpoints/tenantSecrets";
import { qk } from "../../api/queryKeys";
import { Base64Field } from "../../components/forms/Base64Field";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import type { TenantSecretCreate } from "../../api/types/tenantSecret";

interface TenantSecretCreateFormProps {
  accountSlug: string;
  tenantSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const REF_RE = /^[a-z0-9][a-z0-9_:-]{0,63}$/;
const EMPTY = { ref: "", plaintext: "" };

export function TenantSecretCreateForm({
  accountSlug,
  tenantSlug,
  onDone,
  onPendingChange,
}: TenantSecretCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState(EMPTY);

  const refError =
    values.ref === "" || REF_RE.test(values.ref)
      ? null
      : "Ref accepts a-z / 0-9 / hyphen / underscore / colon; 1–64 chars; cannot start with hyphen.";

  const mutation = useMutation({
    mutationFn: () => {
      const body: TenantSecretCreate = {
        ref: values.ref,
        plaintext: values.plaintext,
      };
      return tenantSecrets.create(accountSlug, tenantSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.tenantSecrets(accountSlug, tenantSlug),
      });
      setValues(EMPTY); // Base64Field resets via useEffect when value="".
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.ref !== "" && REF_RE.test(values.ref) && values.plaintext !== "";

  return (
    <FormShell
      submitLabel="Create secret"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create secret failed"
    >
      <TextField
        label="Ref"
        value={values.ref}
        onChange={(ref) => setValues((v) => ({ ...v, ref }))}
        required
        placeholder="llat"
        helper="Operator-meaningful label. Convention: llat, ssid:guest:passphrase, radius_shared, …"
        error={refError}
      />
      <Base64Field
        label="Plaintext secret"
        value={values.plaintext}
        onChange={(plaintext) => setValues((v) => ({ ...v, plaintext }))}
        required
        placeholder="Paste raw secret material…"
      />
    </FormShell>
  );
}
